import { useState, type ReactNode } from 'react'

import MenuSingle from '@/components/ui/MenuSingle'
import SelectTrigger from '@/components/ui/SelectTrigger'
import { cn } from '@/lib/utils'

/**
 * The set `2650:30609` draws. Restated rather than imported from
 * `@/schemas/table`: nothing under `components/ui` reaches into the schema
 * layer, and a table wanting another set passes `pageSizes`.
 */
const DEFAULT_PAGE_SIZES = [10, 20, 50, 100] as const

type PageSlot = number | 'gap-start' | 'gap-end'

/**
 * The window the frames draw: five page numbers at most, first and last always
 * present, an ellipsis wherever the run breaks.
 *
 * Holding the NUMBER of slots steady rather than the window width is what stops
 * the pager from changing width as the user walks through it — at page 1 the
 * budget is spent on 1 2 3 4 … 28, in the middle on 1 … 12 13 14 … 28.
 */
const pageSlots = (page: number, pageCount: number): PageSlot[] => {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1)

  const left = Math.max(page - 1, 1)
  const right = Math.min(page + 1, pageCount)
  const gapBefore = left > 2
  const gapAfter = right < pageCount - 1

  if (!gapBefore) return [1, 2, 3, 4, 'gap-end', pageCount]
  if (!gapAfter) return [1, 'gap-start', pageCount - 3, pageCount - 2, pageCount - 1, pageCount]
  return [1, 'gap-start', left, page, right, 'gap-end', pageCount]
}

/**
 * The phone window: the current page and its two neighbours, and nothing else.
 *
 * Seven 44px slots plus Previous and Next do not fit inside a 375px card, and
 * a pager that overflows its own row is a pager nobody can reach the end of.
 * Always a SUBSET of `pageSlots` — the wide window already contains page-1,
 * page and page+1 at every position — so the two renders never disagree about
 * which numbers exist, and the phone view is the wide one with slots hidden
 * rather than a second pager built from different data.
 */
const compactSlots = (page: number, pageCount: number): ReadonlySet<number> => {
  const start = Math.min(Math.max(page - 1, 1), Math.max(pageCount - 2, 1))
  const end = Math.min(start + 2, pageCount)
  const window = new Set<number>()
  for (let n = start; n <= end; n += 1) window.add(n)
  return window
}

interface PaginationProps {
  /** 1-based, to match the numbers the pager prints. */
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  pageSizes?: readonly number[]
  /** Plural noun for the screen-reader summary: "rows", "trades", "issues". */
  unit?: string
  /**
   * Draws this at the start of the row instead of "Rows per page": Market depth's foot is a
   * count ("Showing 6 of 24 selected companies") over a fixed page size, the mock's.
   * `onPageSizeChange` is then never called.
   */
  lead?: ReactNode
  /**
   * `boxed` (default): the mock's `mkb-pg` buttons. `text`: the trading pages' `mkc-tr__pages`,
   * words only — Previous, the numbers and Next in the secondary grey, lighting white on hover, the
   * current page white at 600 — 24 apart.
   */
  skin?: 'boxed' | 'text'
  /**
   * The boxed skin's size. `md` (default) is the Market pager (`mkb-pg`): 32px boxes, 12px words,
   * a press drops it 1px. `lg` (sz:kit) is the Signal Zone and Public Issues pager (`sz-pager`,
   * `pi-pgbtn`): 36px boxes (the mock's 34, on the grid) 12px of side padding, 14px words, a press
   * scales it to .96, and a 36px rows-per-page select. The text skin ignores it.
   */
  size?: 'md' | 'lg'
  className?: string
}

/**
 * The foot of every Market table: rows-per-page on the left, the numbered
 * pager on the right.
 *
 * PLACEMENT, settled 2026-09-24 — one convention, do not re-split it. The
 * pager is the foot of ITS TABLE: it sits outside the table's card, directly
 * under it, and it draws its OWN separation (`mt-6` below). A caller drops
 * `<Pagination />` beneath a table and gets the right gap without knowing this
 * rule exists, which is the whole point — the old contract supplied nothing
 * and leaned on 19 callers to each remember, and `/brokers/large-trades`
 * forgot (see the third NOT below for how).
 *
 * Outside the card rather than an in-card footer because `DataTable` IS the
 * card: its root is `rounded-lg border border-border-subtle bg-card` with
 * `overflow-hidden`, so 18 of the 19 call sites physically cannot render the
 * pager inside it without `DataTable` growing a footer slot. The frames agree
 * where it counts — `02-all-brokers.md:19` draws `page-foot` as a sibling of
 * `table-panel`, and the Events frame puts the pager below and outside the
 * card. `07-large-trades.md:19` draws the opposite (`trades-foot` nested in
 * the card) and that is precisely the screen that shipped broken; honouring it
 * is a `DataTable` API change, not a margin.
 *
 * `mt-6`, 24px: the pages revamp's one table-to-pager seam (2026-10-27), the
 * same 24 the screen stack's `gap-6` puts between blocks, so a pager reads as
 * the foot of its table and not as a block of its own. It was 16 before.
 *
 * A caller must NOT:
 *  - pass spacing through `className` (`mt-*`, `py-*`, a `border-t` rule).
 *    Two callers doing that by hand is how the convention split; both have
 *    been stripped. `cn` is tailwind-merge, so an override still WINS if a
 *    screen has a real reason — it just has to be a reason worth a comment.
 *  - lean on a parent `flex-col gap-*` to separate pager from table. A flex
 *    `gap` ADDS to this margin rather than absorbing it, so the pair belongs
 *    in a container that gaps neither. That is why the call sites group table
 *    and pager instead of letting the screen stack's `gap-6` fall between
 *    them — the group is also the truer structure: the pager belongs to its
 *    table, not to the page.
 *  - assume a `gap-*` set on a `SectionCard` reaches it. It does not. Given a
 *    header, `SectionCard` wraps its children in a plain `<div className=
 *    "mt-3">`, so the card's flex gap spaces the header against that WRAPPER
 *    and never the siblings inside it. `TradesCard` set `flex flex-col gap-4`
 *    and got zero — the pager rendered flush against the last row, which is
 *    the "padding?" the owner flagged.
 *
 * Controlled — it owns no pagination state, so a table's page can live in the
 * route's search params and a shared link reopens the same page. The one piece
 * of state here is whether the rows-per-page menu is open, which is this
 * component's own chrome and nothing a caller or a URL has an opinion about.
 *
 * Previous and Next stay in the DOM and in the tab order at the ends, marked
 * `aria-disabled`. Removing them would shift the whole pager sideways on the
 * first and last page, and `disabled` would drop the control out from under a
 * keyboard user mid-walk with no announcement of why.
 *
 * Below `lg` every control grows to the 44px touch minimum and the numbers
 * thin out to three. The 30px frames' pager is a mouse target sized for a
 * desktop table foot; on a phone the same row has to be hit with a thumb, and
 * it is a row of its own, so growing it reflows nothing beside it.
 */
export default function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizes = DEFAULT_PAGE_SIZES,
  unit = 'rows',
  lead,
  skin = 'boxed',
  size = 'md',
  className,
}: PaginationProps) {
  const [sizeOpen, setSizeOpen] = useState(false)

  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const atStart = page <= 1
  const atEnd = page >= pageCount
  const compact = compactSlots(page, pageCount)

  const large = size === 'lg'

  // Every control here wears the mock's white control ring (sz:kit, owner ruling A2).
  const ring =
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring-control'

  // `md`: 30 → h-8, 26 → min-w-7 (4-grid): the 30px frames' pager row snapped to h-8, same as
  // ProfileScreen's contact row. `lg`: the Signal pager's 34 → h-9, its 14px words.
  const words = large ? 'text-body-m' : 'text-label-m'
  const stepClasses = cn(
    'inline-flex h-11 shrink-0 items-center rounded-sm border border-border-subtle bg-muted px-3 text-muted-foreground transition-[color,border-color,translate,scale]',
    words,
    ring,
    large ? 'lg:h-9' : 'lg:h-8',
  )

  // The mock's pager (`mkb-pg`): a live button lights its words and its edge, and gives 1px
  // under a press. The fill never changes. A dead end keeps its place at .45. The large pager
  // (`sz-pager`) scales to .96 under a press instead, where motion is welcome.
  const liveClasses = cn(
    'cursor-pointer hover:border-border-strong hover:text-foreground focus-visible:border-border-strong focus-visible:text-foreground',
    large ? 'motion-safe:active:scale-96' : 'active:translate-y-px',
  )

  const slotClasses = cn(
    'inline-flex h-11 min-w-11 shrink-0 items-center justify-center',
    large ? 'px-3 lg:h-9 lg:min-w-9' : 'px-2 lg:h-8 lg:min-w-7',
  )

  // The text pager: no boxes, the words carry it. Below `lg` the 44px targets stay.
  const textClasses = cn(
    'inline-flex h-11 min-w-11 shrink-0 items-center justify-center text-label-m tnum transition-colors lg:h-8 lg:min-w-0',
    ring,
  )
  const text = skin === 'text'

  const go = (next: number, blocked: boolean) => () => {
    if (blocked) return
    onPageChange(next)
  }

  return (
    <div
      className={cn(
        // The separation this component owes the table above it — see
        // PLACEMENT. It is the component's to draw, never the caller's.
        'mt-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-3 lg:gap-y-4',
        className,
      )}
    >
      {lead !== undefined ? (
        lead
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-body-s text-muted-foreground-subtle">Rows per page</span>
          {/* A menu rather than the native picker SelectChip wraps, against the
            usual rule. `Menu / Compact · Web` (2650:29720) is a component the
            design draws in full, and the "Rows per page open" frame
            (2650:30609) puts it at 92px above the trigger with an accent check
            on the current value. A native <select>'s list is OS chrome no
            stylesheet reaches, so on this palette it opens white with a blue
            system highlight — the one thing the frame is not.

            The label stays outside the trigger because the frame draws it
            there; the trigger reads "10", so `aria-label` carries the subject
            the <select> used to own plus the value now shown. */}
          <MenuSingle
            options={pageSizes.map((size) => ({ value: String(size), label: String(size) }))}
            value={String(pageSize)}
            onChange={(next) => {
              onPageSizeChange(Number.parseInt(next, 10))
            }}
            open={sizeOpen}
            onOpenChange={setSizeOpen}
            label="Rows per page"
            // Four numbers read at a glance; a field to search them would be
            // longer than the list. Without one MenuSingle wires up its own
            // typeahead, which is the platform behaviour the <select> gave away.
            searchable={false}
            className="w-23"
            trigger={(triggerProps) => (
              <SelectTrigger
                {...triggerProps}
                aria-label={`Rows per page, ${String(pageSize)}`}
                label={String(pageSize)}
                open={sizeOpen}
                // 32px on desktop (4-grid; was 30px), overriding SelectTrigger's
                // 36px — that height exists to line up with filter-row tabs,
                // and this one has to line up with the pager buttons opposite
                // it instead.
                //
                // The 16px type floor SelectChip needed goes with the <select>:
                // iOS Safari zooms the page when a small FORM control takes
                // focus, and a button is not one. The 44px touch minimum below
                // `lg` still stands and is SelectTrigger's own `h-11`, as are the
                // sheet's 44px rows at that width.
                className={large ? 'lg:h-9' : 'lg:h-8'}
              />
            )}
          />
        </div>
      )}

      <nav aria-label="Pagination">
        <p className="sr-only">
          Page {page} of {pageCount}, {total} {unit}
        </p>
        <ul
          className={cn(
            'flex flex-wrap items-center justify-end',
            text ? 'gap-x-2 lg:gap-x-6' : 'gap-2',
          )}
        >
          <li>
            <button
              type="button"
              aria-disabled={atStart}
              onClick={go(page - 1, atStart)}
              className={
                text
                  ? cn(
                      textClasses,
                      'text-muted-foreground',
                      atStart ? 'cursor-default' : 'cursor-pointer hover:text-foreground',
                    )
                  : cn(stepClasses, atStart ? 'cursor-default opacity-45' : liveClasses)
              }
            >
              Previous
            </button>
          </li>

          {pageSlots(page, pageCount).map((slot) =>
            typeof slot === 'number' ? (
              // `hidden lg:inline-flex` rather than a shorter array: the slot
              // still exists for a desktop user who resizes, and the phone
              // window never has to be recomputed as a second source of truth.
              <li key={slot} className={compact.has(slot) ? undefined : 'hidden lg:block'}>
                <button
                  type="button"
                  {...(slot === page ? { 'aria-current': 'page' as const } : {})}
                  aria-label={`Page ${String(slot)}`}
                  onClick={go(slot, slot === page)}
                  className={
                    text
                      ? cn(
                          textClasses,
                          slot === page
                            ? 'cursor-default text-h-xs text-foreground'
                            : 'cursor-pointer text-muted-foreground hover:text-foreground',
                        )
                      : cn(
                          slotClasses,
                          'rounded-sm border tnum transition-[color,border-color,translate,scale]',
                          words,
                          ring,
                          // The page on screen is the accent, with no hover: it goes nowhere.
                          slot === page
                            ? cn(
                                'cursor-default border-accent bg-accent text-accent-foreground',
                                large ? 'text-h-s' : 'text-h-xs',
                              )
                            : ['border-border-subtle bg-muted text-muted-foreground', liveClasses],
                        )
                  }
                >
                  {slot}
                </button>
              </li>
            ) : (
              <li
                key={slot}
                aria-hidden="true"
                className={cn(slotClasses, 'hidden text-muted-foreground lg:inline-flex', words)}
              >
                …
              </li>
            ),
          )}

          <li>
            <button
              type="button"
              aria-disabled={atEnd}
              onClick={go(page + 1, atEnd)}
              className={
                text
                  ? cn(
                      textClasses,
                      'text-muted-foreground',
                      atEnd ? 'cursor-default' : 'cursor-pointer hover:text-foreground',
                    )
                  : cn(stepClasses, atEnd ? 'cursor-default opacity-45' : liveClasses)
              }
            >
              Next
            </button>
          </li>
        </ul>
      </nav>
    </div>
  )
}
